import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Flag, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { REPORT_EXERCISE_PROBLEM } from "@/graphql/mutations/sessions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MAX_REPORT_COMMENT, REPORT_REASONS, reportReason } from "@/lib/report-reasons";

type Props = {
  sessionId: string;
  exerciseId: string;
  /** Already reported — restored from `exercise.reportedAt` across reloads. */
  reported: boolean;
  /** Opened from the question's own „…" menu; this component has no trigger. */
  open: boolean;
  onClose: () => void;
  onReported: (exerciseId: string) => void;
};

/** Show the counter only near the limit — one on an optional field reads as a requirement. */
const COUNTER_FROM = MAX_REPORT_COMMENT - 100;

/**
 * "Something is wrong with this question."
 *
 * The child who is stuck on a question is the only person who has read it
 * carefully under real conditions, and until now the only thing they could say
 * about a broken one was to get it wrong. This is deliberately *not* the
 * „manau, mano atsakymas teisingas" button next to it: that disputes the
 * grading of a working question, this reports a question that does not work.
 *
 * Quiet by default — a prominent "report" link invites a bored child to press
 * it, and every false report costs a grown-up a real review. Which is why the
 * trigger lives in the question's „…" menu rather than here: the panel is
 * opened from there, and this component only ever renders the form and the
 * acknowledgement.
 *
 * **Two steps, and the second one is asked rather than offered.** It opens into
 * reasons, because "what exactly is wrong" is hard to write and easy to pick,
 * and the picked reason is the part an admin can sort by. But a reason alone
 * often cannot be acted on — „yra rašybos klaida" over a question of forty words
 * sends a grown-up hunting — so once one is picked the panel asks the question
 * that reason implies („Kurį žodį reikėtų pataisyti?"). A named question is
 * answerable in four words where „gali parašyti plačiau" is a blank page.
 */
export function ReportProblem({ sessionId, exerciseId, reported, open, onClose, onReported }: Props) {
  const [reason, setReason] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [report] = useMutation(REPORT_EXERCISE_PROBLEM);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const picked = reportReason(reason);
  const trimmed = comment.trim();
  // Only „Kita" owes words: it names nothing by itself, so an admin opening one
  // learns exactly what they knew before. Every other reason is already a
  // finding, and requiring a sentence would turn a five-second report into
  // homework — which is how you teach a child not to bother.
  const missingWords = Boolean(picked?.needsComment) && trimmed.length === 0;
  const canSend = Boolean(picked) && !missingWords && !sending;

  // „Kita" is the one reason whose field is the answer, so it is the one that
  // gets the keyboard. Anywhere else an auto-focused box becomes the default
  // answer for a child who has already said everything they wanted to say.
  useEffect(() => {
    if (open && picked?.needsComment) commentRef.current?.focus();
  }, [open, picked?.needsComment]);

  // The panel is opened from the „…" menu at the top of the question, and on a
  // long one it lands below the fold — so pressing "something is wrong" would
  // appear to do nothing. `nearest` leaves an already-visible panel where it is.
  useEffect(() => {
    if (open) panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [open]);

  // The acknowledgement outlives the panel and is not gated on `open`: it is the
  // only thing telling the child their report went somewhere, and it has to
  // survive a reload (restored from `exercise.reportedAt`).
  if (reported) {
    return (
      <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
        <Check className="h-3.5 w-3.5" />
        Ačiū! Suaugęs peržiūrės šią užduotį
      </div>
    );
  }

  if (!open) return null;

  async function send() {
    if (!reason || !canSend) return;
    setSending(true);
    try {
      const { data } = await report({
        variables: { sessionId, exerciseId, reason, comment: trimmed || null },
      });
      if ((data as any)?.reportExerciseProblem?.success) {
        onReported(exerciseId);
        onClose();
      } else {
        const errors = (data as any)?.reportExerciseProblem?.errors ?? [];
        toast.error(errors[0] || "Nepavyko išsiųsti. Pabandyk dar kartą.");
      }
    } catch {
      // Never an error page in front of a child who was trying to help.
      toast.error("Nepavyko išsiųsti. Pabandyk dar kartą.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      ref={panelRef}
      className="mt-3 rounded-lg border bg-muted/40 p-3 space-y-3"
      // Escape is how everything else in this app closes; a child who opened
      // this by accident should not have to find „Atšaukti".
      onKeyDown={(e) => {
        if (e.key === "Escape" && !sending) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-center gap-1.5 text-sm font-medium">
        <Flag className="h-3.5 w-3.5" />
        Kas negerai su šia užduotimi?
      </div>

      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Kas negerai su šia užduotimi?">
        {REPORT_REASONS.map((r) => {
          const Icon = r.icon;
          const active = reason === r.key;
          return (
            <button
              key={r.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setReason(r.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                active
                  ? "border-indigo-500 bg-indigo-100 text-indigo-900 dark:bg-indigo-900/50 dark:text-indigo-100"
                  : "bg-background hover:bg-muted"
              }`}
            >
              {active ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5 opacity-70" />}
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Nothing to write about until there is something to write about: an empty
          box under six unpicked chips is one more thing to read past. */}
      {picked && (
        <div className="space-y-1.5">
          <label htmlFor={`report-comment-${exerciseId}`} className="block text-sm">
            {picked.prompt}
            {!picked.needsComment && <span className="text-muted-foreground"> (nebūtina)</span>}
          </label>
          <Textarea
            id={`report-comment-${exerciseId}`}
            ref={commentRef}
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_REPORT_COMMENT))}
            placeholder={picked.needsComment ? "Parašyk savais žodžiais" : "Kelių žodžių užtenka"}
            rows={2}
            className="text-sm"
          />
          {comment.length >= COUNTER_FROM && (
            <div className="text-right text-xs text-muted-foreground">
              {comment.length} / {MAX_REPORT_COMMENT}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={send} disabled={!canSend}>
          {sending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Siųsti
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} disabled={sending}>
          Atšaukti
        </Button>
        {/* A disabled button with nothing beside it is a dead end — the one
            thing this app has learned twice. Say what is missing. */}
        {!picked && <span className="text-xs text-muted-foreground">Pasirink, kas negerai</span>}
        {missingWords && <span className="text-xs text-muted-foreground">Įrašyk kelis žodžius</span>}
      </div>
    </div>
  );
}
